import { useState } from 'react';
import { ShieldCheck } from '@phosphor-icons/react';
import type { MappedApiError } from '@mybusinessmate/api-client';
import { CAPTION_MAX, FIX_REASON_MIN, type TaskDetail, type Version } from '@mybusinessmate/schema';
import {
  deliverableFrom,
  emptySubmitValues,
  fromDeliverable,
  type SubmitErrors,
  type SubmitValues,
} from '../../lib/submit-form';
import { describeChange } from '../../lib/text-diff';
import { useFixVersion } from '../../lib/tasks-api';
import { Icon } from '../Icon';
import { Button } from '../ui/button';
import { DateField, TimeField } from '../ui/date-field';
import { Drawer } from '../ui/dialog';
import { Input } from '../ui/input';
import { TagInput } from '../ui/tag-input';
import { TextArea } from '../ui/text-area';
import { useToast } from '../ui/toast';
import { UpdateFields, WebsiteDetailsFields } from './submit-fields';

const fieldError = (errors: SubmitErrors, name: string) => errors[`deliverable.${name}`];

/**
 * Edit a version that was already sent (Figma A05.4b "Edit version 2 in place", A05.4c "Fix version 2"). A lead,
 * the account manager or an admin changes it where it stands, silently; a specialist's change is only proposed and
 * waits for a lead. Either way a reason is required and goes in the log.
 */
export function FixVersionDrawer({
  open,
  onClose,
  task,
  version,
  inPlace,
}: {
  open: boolean;
  onClose: () => void;
  task: TaskDetail;
  version: Version;
  /** The caller may edit in place; otherwise they propose. */
  inPlace: boolean;
}) {
  const toast = useToast();
  const fix = useFixVersion(task.id);
  const start = fromDeliverable(emptySubmitValues(task.progress), version.details);
  const [values, setValues] = useState<SubmitValues>(start);
  const [reason, setReason] = useState('');
  const [errors, setErrors] = useState<SubmitErrors>({});
  const [formError, setFormError] = useState<string>();
  const change = (patch: Partial<SubmitValues>) => setValues((v) => ({ ...v, ...patch }));

  const number = version.versionNumber;
  const captionDiff = describeChange(start.caption, values.caption);
  const lead = task.team.find((m) => m.staffRole === 'lead_specialist')?.name;

  const save = async () => {
    setFormError(undefined);
    if (reason.trim().length < FIX_REASON_MIN) {
      setErrors({ reason: 'Say what you are fixing' });
      return;
    }
    setErrors({});
    try {
      await fix.mutateAsync({
        subId: version.id,
        input: { reason: reason.trim(), deliverable: deliverableFrom(values, task.taskType) },
      });
      toast({
        title: inPlace ? `Saved over v${number}` : 'Saved as a draft fix',
        description: inPlace ? 'The client wasn’t told.' : `${lead ?? 'The lead'} will look at it.`,
        tone: 'success',
      });
      onClose();
    } catch (err) {
      const e = err as Partial<MappedApiError>;
      if (e.kind === 'validation' && e.fieldErrors?.length) {
        setErrors(Object.fromEntries(e.fieldErrors.map((f) => [f.field, f.message])));
        setFormError(e.fieldErrors[0]!.message);
      } else if (e.status === 409) {
        setFormError(e.message ?? 'Someone else is already changing this version.');
      } else {
        setFormError('We couldn’t save that. Please try again.');
      }
    }
  };

  const post = task.taskType === 'post' || task.taskType === 'reel';
  const fieldProps = { values, onChange: change, errors, businessId: task.businessId ?? '', taskId: task.id };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      size="form"
      title={inPlace ? `Edit version ${number} in place` : `Fix version ${number}`}
      description={
        inPlace
          ? 'For small fixes like a typo or a wrong date. For anything bigger, submit a new version.'
          : `Small fixes only. Your change is saved as a draft until ${lead ? `your lead` : 'a lead'} approves it.`
      }
      footer={
        <>
          <Button variant="secondary" className="sm:flex-1" onClick={onClose}>
            Cancel
          </Button>
          <Button className="sm:flex-1" loading={fix.isPending} onClick={() => void save()}>
            {inPlace ? `Save fix to v${number}` : 'Save draft fix'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-(--mbm-space-5)">
        <p className="flex items-start gap-(--mbm-space-3) rounded-(--mbm-radius-md) bg-surface-tint p-(--mbm-space-4) text-body-small text-text-secondary">
          <span className="mt-(--mbm-space-0-5) shrink-0 text-text-primary">
            <Icon icon={ShieldCheck} size="md" />
          </span>
          <span>
            {inPlace
              ? 'Saved over the version with an “Edited” tag. The client isn’t asked to approve again. Who changed what is kept in the audit log.'
              : `Saved as a draft fix. ${lead ? `${lead} (lead)` : 'A lead'} has to approve it before the client sees the change. Until then the client keeps seeing v${number} as it is now.`}
          </span>
        </p>
        {formError ? (
          <p role="alert" className="text-body-small text-feedback-error">
            {formError}
          </p>
        ) : null}

        {post ? (
          <>
            <div className="flex flex-col gap-(--mbm-space-1-5)">
              <TextArea
                label="Caption"
                density="compact"
                rows={5}
                value={values.caption}
                maxLength={CAPTION_MAX}
                labelAside={captionDiff ? <span className="text-body-xsmall text-brand-primary">1 change</span> : null}
                error={fieldError(errors, 'caption')}
                onChange={(e) => change({ caption: e.target.value })}
              />
              {captionDiff ? <p className="text-body-xsmall text-feedback-success-text">{captionDiff}</p> : null}
            </div>
            <div className="flex flex-col gap-(--mbm-space-2)">
              <span className="text-label-medium text-text-primary">Hashtags</span>
              <TagInput
                variant="filled"
                value={values.hashtags}
                onChange={(hashtags) => change({ hashtags })}
                placeholder="Type a hashtag and press Enter"
              />
            </div>
            <div className="grid grid-cols-2 gap-(--mbm-space-3)">
              <DateField
                label="Publish on"
                value={values.publishDate}
                onChange={(publishDate) => change({ publishDate })}
              />
              <TimeField
                label="Time"
                value={values.publishTime}
                disabled={!values.publishDate}
                onChange={(publishTime) => change({ publishTime })}
              />
            </div>
          </>
        ) : task.taskType === 'website' ? (
          <WebsiteDetailsFields {...fieldProps} zip={null} />
        ) : (
          <UpdateFields {...fieldProps} custom={task.taskType === 'custom'} />
        )}

        <Input
          label="What are you fixing?"
          variant="filled"
          density="compact"
          value={reason}
          placeholder="e.g. Javed asked to mention home delivery in chat"
          error={errors.reason}
          labelAside={<span className="text-body-xsmall text-text-tertiary">Required · goes in the log</span>}
          onChange={(e) => setReason(e.target.value)}
        />
      </div>
    </Drawer>
  );
}
